'use client';

import { useEffect, useRef, useState } from 'react';
import Attach from '@fluentui/svg-icons/icons/attach_24_regular.svg';
import Document from '@fluentui/svg-icons/icons/document_24_regular.svg';
import DocumentAdd from '@fluentui/svg-icons/icons/document_add_24_regular.svg';
import ImageIcon from '@fluentui/svg-icons/icons/image_add_24_regular.svg';
import { useTranslations } from 'next-intl';
import { useDropzone } from 'react-dropzone';
import type { Accept } from 'react-dropzone';

type Props = {
  required?: boolean;
  label: string;
  name?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  preloadedFile?: File | string;
  messages?: string | string[];
  fileType?: 'pdf' | 'png';
};

const getFileNameParts = (filename: string) => {
  if (!filename) return;
  const lastDotIndex = filename.lastIndexOf('.');
  if (lastDotIndex === -1) return { baseName: filename, extension: '' };
  return {
    baseName: filename.slice(0, lastDotIndex),
    extension: filename.slice(lastDotIndex),
  };
};
export default function FileInput(props: Props) {
  const {
    required,
    label,
    name,
    onChange,
    preloadedFile,
    messages,
    fileType = 'pdf',
    ...otherProps
  } = props;
  const t = useTranslations('FileInput');

  const hiddenInputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>(
    preloadedFile
      ? typeof preloadedFile === 'string'
        ? []
        : [preloadedFile]
      : [],
  );
  const [preloadedFileName, setPreloadedFileName] = useState<string | null>(
    typeof preloadedFile === 'string' ? preloadedFile : null,
  );

  const acceptConfig: Accept =
    fileType === 'pdf'
      ? { 'application/pdf': ['.pdf'] }
      : { 'image/png': ['.png'] };

  const hasFile = files.length > 0 || !!preloadedFileName;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (incomingFiles) => {
      // Only take the first file to prevent multiple selection
      const singleFile = incomingFiles.slice(0, 1);
      setFiles(singleFile);
      setPreloadedFileName(null); // Clear preloaded filename when new file is selected
      if (hiddenInputRef.current) {
        const dataTransfer = new DataTransfer();
        singleFile.forEach((file) => {
          if (file instanceof File) dataTransfer.items.add(file);
        });
        hiddenInputRef.current.files = dataTransfer.files;

        const event = new Event('change', { bubbles: true });
        hiddenInputRef.current.dispatchEvent(event);

        if (onChange) {
          onChange({
            target: hiddenInputRef.current,
            type: 'change',
          } as React.ChangeEvent<HTMLInputElement>);
        }
      }
    },
    accept: acceptConfig,
    multiple: false,
    disabled: hasFile, // Disable dropzone when a file is already selected
  });

  useEffect(() => {
    if (preloadedFile) {
      if (typeof preloadedFile === 'string') {
        setPreloadedFileName(preloadedFile);
        setFiles([]);
      } else if (preloadedFile instanceof File) {
        setPreloadedFileName(null);
        setFiles([preloadedFile]);
        if (hiddenInputRef.current) {
          const dataTransfer = new DataTransfer();
          dataTransfer.items.add(preloadedFile);
          hiddenInputRef.current.files = dataTransfer.files;
        }
      }
    }
  }, [preloadedFile]);

  const handleRemoveFile = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setFiles([]);
    setPreloadedFileName('');
    if (hiddenInputRef.current) {
      hiddenInputRef.current.value = '';

      const event = new Event('change', { bubbles: true });
      hiddenInputRef.current.dispatchEvent(event);

      if (onChange) {
        onChange({
          target: hiddenInputRef.current,
          type: 'change',
        } as React.ChangeEvent<HTMLInputElement>);
      }
    }
  };

  const className = `h-40 rounded-lg border border-outline border-dashed flex flex-col justify-center items-center ${hasFile ? 'cursor-default' : 'cursor-pointer'}`;

  return (
    <div className="flex w-full flex-col gap-2">
      <p className="text-sm font-medium leading-6">{label}</p>
      <div {...getRootProps({ className })}>
        <input
          type="file"
          required={required}
          // accept={fileType === "pdf" ? "application/pdf" : "image/png"}
          style={{
            opacity: 0,
            position: 'absolute',
            zIndex: -1,
          }}
          ref={hiddenInputRef}
        />
        <input {...getInputProps({ onChange, ...otherProps })} />
        {files.length > 0 || preloadedFileName ? (
          <div className="flex flex-col items-center justify-center gap-2">
            <Document className="fill-gray-500" />
            <ul>
              {preloadedFileName ? (
                <li>
                  <p className="flex w-full max-w-64">
                    <span className="overflow-hidden truncate whitespace-nowrap text-gray-500">
                      {preloadedFileName}
                    </span>
                  </p>
                </li>
              ) : (
                files.map((file, index) => {
                  const { baseName, extension } = getFileNameParts(
                    file.name,
                  ) || {
                    baseName: '',
                    extension: '',
                  };
                  return (
                    <li key={index}>
                      <p className="flex w-full max-w-64">
                        <span className="overflow-hidden truncate whitespace-nowrap text-gray-500">
                          {baseName}
                        </span>
                        <span className="text-gray-500">{extension}</span>
                      </p>
                    </li>
                  );
                })
              )}
            </ul>
            <button
              onClick={(e) =>
                handleRemoveFile(e as React.MouseEvent<HTMLButtonElement>)
              }
              className="text-blue-600"
            >
              {t('remove')}
            </button>
          </div>
        ) : isDragActive ? (
          <div className="flex flex-col items-center justify-center gap-2">
            {fileType === 'pdf' ? (
              <DocumentAdd className="fill-gray-500" />
            ) : (
              <ImageIcon className="fill-gray-500" />
            )}
            <p className="text-gray-500">{t('dragActive')}</p>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-2">
            {fileType === 'pdf' ? (
              <Attach className="fill-gray-500" />
            ) : (
              <ImageIcon className="fill-gray-500" />
            )}
            <p className="text-gray-500">
              {t(fileType === 'pdf' ? 'emptyPdf' : 'emptyImagePng')}
            </p>
          </div>
        )}
      </div>
      {/* {messages && ( */}
      <ul className="px-4 pt-1 text-xs text-red-600">
        {(Array.isArray(messages) ? messages : [messages]).map(
          (message, index) => (
            <li key={index}>{message}</li>
          ),
        )}
      </ul>
      {/* )} */}
    </div>
  );
}
